import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { canEditScenario, COLLEAGUE_EDIT_ENABLED } from "@/lib/scenarios/editAccess";
import { CircleCheck, Copy, Eye, EyeOff, Info, Link as LinkIcon, Loader2, Package, Pencil, PlayCircle, Sparkles, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { withCacheBust } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { powtoonBE } from "@/lib/powtoon-be/client";
import type { Scenario } from "@/lib/powtoon-be/types";
import type { PowtoonUserSummary } from "@/lib/powtoon-be/users";
import { deriveScenarioBackground, deriveScenarioThumbnail } from "@/lib/scenarios/providers";
import { interactionModeOf } from "@/lib/scenarios/providers/spec";
import { getMode, type InteractionMode } from "@/lib/scenarios/interactionModes";
import { MODE_VISUALS } from "@/lib/scenarios/modeVisuals";
import { downloadScormPackage } from "@/lib/scorm/downloadScormPackage";
import { toast } from "sonner";
import { ShareScenarioModal } from "./ShareScenarioModal";

const PUBLISH_VISUALS: Record<string, { icon: typeof CircleCheck; label: string; tooltip: string }> = {
  full: { icon: CircleCheck, label: "Full", tooltip: "Fully published in Powtoon workspace, with complete access" },
  demo: { icon: Eye, label: "Demo", tooltip: "A limited preview version to try-out from the workspace" },
  hidden: { icon: EyeOff, label: "Hidden", tooltip: "Not shown in Powtoon workspace" },
  upsell: { icon: Sparkles, label: "Upsell", tooltip: "Published to the workspace as an interactive upsell banner" },
};

const RELATIVE_FORMATTER = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function formatLastEdited(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const diffSec = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 60 * 60 * 24 * 365],
    ["month", 60 * 60 * 24 * 30],
    ["week", 60 * 60 * 24 * 7],
    ["day", 60 * 60 * 24],
    ["hour", 60 * 60],
    ["minute", 60],
  ];
  for (const [unit, sec] of units) {
    if (abs >= sec) return RELATIVE_FORMATTER.format(Math.round(diffSec / sec), unit);
  }
  return RELATIVE_FORMATTER.format(diffSec, "second");
}

export function ScenarioCard({
  scenario, readOnly = false, owner,
}: {
  scenario: Scenario;
  readOnly?: boolean;
  /** Resolved owner of a scenario shared by someone else in the company. */
  owner?: PowtoonUserSummary | null;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = usePowtoonAuth();
  // Shared avatars: a colleague `can_edit` share unlocks the Edit button only.
  const sharesQuery = useQuery({
    queryKey: ["scenario-shares", scenario.id],
    queryFn: () => powtoonBE.scenarios.listShares(scenario.id),
    enabled: COLLEAGUE_EDIT_ENABLED && readOnly && user?.powtoonUserId != null,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const canEdit = !readOnly || canEditScenario(scenario, user?.powtoonUserId, sharesQuery.data);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const publishMode = scenario.content?.publish_mode ?? "full";
  const thumbnailUrl = deriveScenarioThumbnail(scenario);
  const backgroundUrl = deriveScenarioBackground(scenario);
  // Provider-agnostic: every provider stores mode on its own block.
  const mode: InteractionMode = interactionModeOf(scenario.content);
  const modeVisual = MODE_VISUALS[mode] ?? MODE_VISUALS.conversational;
  const ModeIcon = modeVisual.icon;
  const publishVisual = PUBLISH_VISUALS[publishMode] ?? PUBLISH_VISUALS.full;
  const PublishIcon = publishVisual.icon;
  const modeTooltip = getMode(mode).description;
  const description = String(scenario.description ?? "").trim();

  const lastEditedIso = scenario.modified ?? scenario.created;
  const lastEditedLabel = formatLastEdited(lastEditedIso);

  const deleteMutation = useMutation({
    mutationFn: () => powtoonBE.scenarios.remove(scenario.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["scenarios"] });
      toast.success("Live avatar deleted");
      setConfirmOpen(false);
    },
    onError: (err: Error) => { toast.error(err.message || "Failed to delete"); },
  });

  const shareMutation = useMutation({
    mutationFn: async () => {
      const { public_link_token } = await powtoonBE.scenarios.createPublicLinkToken(scenario.id);
      const url = `${window.location.origin}/learn/${scenario.id}?public_link_token=${encodeURIComponent(public_link_token)}`;
      await navigator.clipboard.writeText(url);
      return url;
    },
    onSuccess: () => toast.success("Public link copied to clipboard"),
    onError: (err: Error) => toast.error(err.message || "Failed to generate public link"),
  });

  const scormMutation = useMutation({
    mutationFn: async (version: "1.2" | "2004") => {
      const { public_link_token } = await powtoonBE.scenarios.createPublicLinkToken(scenario.id);
      await downloadScormPackage({
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        publicLinkToken: public_link_token,
        version,
      });
    },
    onSuccess: () => toast.success("SCORM package downloaded"),
    onError: (err: Error) => toast.error(err.message || "Failed to build SCORM package"),
  });

  return (
    <div
      className="group flex min-w-[360px] flex-col overflow-hidden rounded-xl border bg-card transition-shadow hover:shadow-md"
    >
      <TooltipProvider delayDuration={200}>
      <div className="relative aspect-video w-full overflow-hidden bg-muted">
        {backgroundUrl && (
          <img
            src={backgroundUrl}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        {thumbnailUrl && (
          <img
            src={withCacheBust(thumbnailUrl, scenario.modified) ?? thumbnailUrl}
            alt=""
            className="relative z-10 h-full w-full object-contain transition-transform group-hover:scale-[1.02]"
          />
        )}
        <div className="absolute bottom-2 left-2 z-20 flex flex-wrap items-center gap-1.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide whitespace-nowrap shadow-sm cursor-help"
                style={{
                  background: modeVisual.activeBg,
                  color: modeVisual.iconFg,
                  borderColor: modeVisual.border,
                }}
              >
                <ModeIcon className="h-3 w-3" />
                {modeVisual.shortLabel}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[220px] bg-popover text-popover-foreground border">{modeTooltip}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-[10px] font-semibold tracking-wide text-foreground whitespace-nowrap shadow-sm cursor-help">
                <PublishIcon className="h-3 w-3" />
                {publishVisual.label}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[220px] bg-popover text-popover-foreground border">{publishVisual.tooltip}</TooltipContent>
          </Tooltip>
          {readOnly && <Badge variant="outline" className="uppercase tracking-wide bg-background shadow-sm">Shared</Badge>}
        </div>
      </div>

      <div className="flex items-center gap-1.5 px-4 pt-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="truncate text-sm font-medium">{scenario.name}</h3>
        {description && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                aria-label="Avatar description"
                className="inline-flex shrink-0 cursor-help items-center text-muted-foreground hover:text-foreground"
              >
                <Info className="h-4 w-4" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px] bg-popover text-popover-foreground border">{description}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {owner && (
        <p className="flex items-center gap-1.5 px-4 pt-1 text-xs text-muted-foreground">
          {owner.thumbUrl && (
            <img
              src={owner.thumbUrl}
              alt=""
              loading="lazy"
              className="h-4 w-4 shrink-0 rounded-full object-cover"
            />
          )}
          <span className="truncate">Shared by {owner.name}</span>
        </p>
      )}
      {lastEditedLabel && (
        <p className="px-4 pt-0.5 text-xs text-muted-foreground">Last edited {lastEditedLabel}</p>
      )}


      <div
        className="flex items-center gap-2 px-3 pt-4 pb-4"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {!readOnly && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to={`/trainer/${scenario.id}`}>
                <Button variant="outline" size="sm" className="gap-1.5 shadow-none">
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent className="max-w-[230px] bg-popover text-popover-foreground border">Edit the avatar — its prompt, knowledge base, voice, opening line and publish settings.</TooltipContent>
          </Tooltip>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to={`/learn/${scenario.id}`}>
                <Button variant="outline" size="icon" className="shadow-none"><PlayCircle className="h-4 w-4" /></Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent className="bg-popover text-popover-foreground border">Start session</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline" size="icon" className="shadow-none"
                onClick={() => shareMutation.mutate()}
                disabled={shareMutation.isPending}
              >
                {shareMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-popover text-popover-foreground border">Share public link</TooltipContent>
          </Tooltip>
          {!readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline" size="icon" className="shadow-none"
                  onClick={() => setShareOpen(true)}
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-popover text-popover-foreground border">Share with team or colleague</TooltipContent>
            </Tooltip>
          )}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline" size="icon" className="shadow-none"
                    disabled={scormMutation.isPending}
                  >
                    {scormMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent className="bg-popover text-popover-foreground border">Download SCORM package</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => scormMutation.mutate("1.2")}>
                SCORM 1.2
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => scormMutation.mutate("2004")}>
                SCORM 2004 (4th Edition)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {!readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline" size="icon" className="shadow-none"
                  onClick={() => navigate(`/trainer/new?from=${scenario.id}`)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-popover text-popover-foreground border">Duplicate avatar</TooltipContent>
            </Tooltip>
          )}
          {!readOnly && (
            <>
              <span aria-hidden className="h-6 w-px bg-border" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline" size="icon"
                    className="shadow-none text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/40"
                    onClick={() => setConfirmOpen(true)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-popover text-popover-foreground border">Delete avatar</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this live avatar?</AlertDialogTitle>
              <AlertDialogDescription>
                “{scenario.name}” will be permanently removed. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); deleteMutation.mutate(); }}
                disabled={deleteMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ShareScenarioModal
          scenarioId={scenario.id}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      </div>
      </TooltipProvider>
    </div>
  );
}
